"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { graphemeLength, segmentGraphemes } from "@/lib/graphemes";
import { t } from "@/lib/i18n/interpolate";

const WHITESPACE_SPLIT_RE = /(\s+)/;
const LETTER_CLUSTER_RE = /^(?:\p{L}\p{M}*)+$/u;
const LETTERS_ONLY_RE = /^(?:\p{L}\p{M}*)+$/u;
const BLANK = "________";
const MIN_BLANK_LENGTH = 4;
const N_OPTIONS = [5, 6, 7, 8, 9, 10, 11, 12];

export type ClozeMode = "nth" | "list";

interface Worksheet {
  text: string;
  answers: string[];
  bank: string[];
}

interface TokenParts {
  lead: string;
  core: string;
  trail: string;
}

export function splitToken(token: string, locale?: string): TokenParts {
  const normalized = token.normalize("NFC");
  const graphemes = segmentGraphemes(normalized, locale);
  const firstLetter = graphemes.findIndex((part) =>
    LETTER_CLUSTER_RE.test(part)
  );
  if (firstLetter === -1) {
    return { lead: normalized, core: "", trail: "" };
  }
  let lastLetter = graphemes.length - 1;
  while (
    lastLetter > firstLetter &&
    !LETTER_CLUSTER_RE.test(graphemes[lastLetter] ?? "")
  ) {
    lastLetter -= 1;
  }
  return {
    lead: graphemes.slice(0, firstLetter).join(""),
    core: graphemes.slice(firstLetter, lastLetter + 1).join(""),
    trail: graphemes.slice(lastLetter + 1).join(""),
  };
}

function isProperNoun(core: string, locale?: string): boolean {
  const [first = "", second = ""] = segmentGraphemes(core, locale);
  if (first.length === 0 || second.length === 0) {
    return false;
  }
  const firstIsUpper =
    first === first.toLocaleUpperCase(locale) &&
    first !== first.toLocaleLowerCase(locale);
  const secondIsLower =
    second === second.toLocaleLowerCase(locale) &&
    second !== second.toLocaleUpperCase(locale);
  return firstIsUpper && secondIsLower;
}

function isNthEligible(core: string, locale?: string): boolean {
  return (
    graphemeLength(core, locale) >= MIN_BLANK_LENGTH &&
    LETTERS_ONLY_RE.test(core) &&
    !isProperNoun(core, locale)
  );
}

function shuffle<T>(items: T[]): T[] {
  const result = items.slice();
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = result[i] as T;
    result[i] = result[j] as T;
    result[j] = a;
  }
  return result;
}

function parseTargetWords(input: string, locale?: string): Set<string> {
  return new Set(
    input
      .split(",")
      .map((word) => word.trim().normalize("NFC").toLocaleLowerCase(locale))
      .filter((w) => w.length > 0)
  );
}

export function buildWorksheet(
  passage: string,
  mode: ClozeMode,
  n: number,
  targetInput: string,
  locale?: string
): Worksheet {
  const targets = parseTargetWords(targetInput, locale);
  const targetList = [...targets];
  const collator = new Intl.Collator(locale, {
    sensitivity: "variant",
    usage: "search",
  });
  const answers: string[] = [];
  let eligibleCount = 0;
  const shouldBlank = (core: string): boolean => {
    if (core.length === 0) {
      return false;
    }
    if (mode === "list") {
      const comparisonCore = core.normalize("NFC").toLocaleLowerCase(locale);
      return targetList.some(
        (target) => collator.compare(target, comparisonCore) === 0
      );
    }
    if (!isNthEligible(core, locale)) {
      return false;
    }
    eligibleCount += 1;
    return eligibleCount % n === 0;
  };
  const text = passage
    .split(WHITESPACE_SPLIT_RE)
    .map((token, index) => {
      if (index % 2 === 1) {
        return token;
      }
      const { lead, core, trail } = splitToken(token, locale);
      if (!shouldBlank(core)) {
        return token;
      }
      answers.push(core);
      return `${lead}${BLANK} (${answers.length})${trail}`;
    })
    .join("");
  return { text, answers, bank: shuffle(answers) };
}

/** English strings. A translated content file overrides these through `ui`. */
export const CLOZE_TEST_GENERATOR_STRINGS = {
  passage: "Passage",
  pasteThePassageYouWant:
    "Paste the passage you want to turn into a worksheet...",
  blankingMode: "Blanking mode",
  everyNthWord: "Every Nth word",
  blankTheseWords: "Blank these words",
  blankEvery: "Blank every",
  nthWordOption: "{n}th word",
  skipNote:
    "Words under {min} letters and capitalized proper-noun-looking words are skipped.",
  wordBankLine: "Word bank: {words}",
  wordsToBlankCommaSeparated:
    "Words to blank — comma-separated, whole-word matching",
  photosynthesisChlorophyllGlucose: "photosynthesis, chlorophyll, glucose",
  regenerate: "Regenerate",
  generateWorksheet: "Generate Worksheet",
  copied: "Copied!",
  copyWorksheet: "Copy Worksheet",
  print: "Print",
  hideWordBank: "Hide word bank",
  showWordBank: "Show word bank",
  hideAnswerKey: "Hide answer key",
  showAnswerKey: "Show answer key",
  noWordsWereBlankedTry:
    "No words were blanked. Try a smaller N, a longer passage, or check\n          that your listed words appear in the text.",
  worksheet: "Worksheet",
  wordBank: "Word Bank",
  answerKey: "Answer Key",
} as const;

export default function ClozeTestGeneratorWidget() {
  const locale = useToolLocale();
  const strings = useToolUi(CLOZE_TEST_GENERATOR_STRINGS);
  const [passage, setPassage] = useState("");
  const [mode, setMode] = useState<ClozeMode>("nth");
  const [n, setN] = useState(7);
  const [targetInput, setTargetInput] = useState("");
  const [worksheet, setWorksheet] = useState<Worksheet | null>(null);
  const [showBank, setShowBank] = useState(true);
  const [showAnswers, setShowAnswers] = useState(false);
  const [copied, setCopied] = useState(false);

  const canGenerate =
    passage.trim().length > 0 &&
    (mode === "nth" || parseTargetWords(targetInput, locale).size > 0);

  function handleGenerate() {
    if (!canGenerate) {
      return;
    }
    setWorksheet(buildWorksheet(passage, mode, n, targetInput, locale));
    setCopied(false);
  }

  function handleCopy() {
    if (!worksheet) {
      return;
    }
    const bankLine = t(strings.wordBankLine, {
      words: worksheet.bank.join(", "),
    });
    writeToClipboard(`${worksheet.text}\n\n${bankLine}`).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Passage */}
      <div className="space-y-2 print:hidden">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="passage-input"
        >
          {strings.passage}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="passage-input"
          onChange={(e) => setPassage(e.target.value)}
          placeholder={strings.pasteThePassageYouWant}
          value={passage}
        />
      </div>
      {/* Mode */}
      <section className="space-y-3 print:hidden">
        <p className="font-medium text-foreground text-sm">
          {strings.blankingMode}
        </p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "nth"}
              className="accent-primary"
              name="cloze-mode"
              onChange={() => setMode("nth")}
              type="radio"
              value="nth"
            />
            {strings.everyNthWord}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "list"}
              className="accent-primary"
              name="cloze-mode"
              onChange={() => setMode("list")}
              type="radio"
              value="list"
            />
            {strings.blankTheseWords}
          </label>
        </div>
        {mode === "nth" && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-foreground text-sm" htmlFor="nth-select">
              {strings.blankEvery}
            </label>
            <select
              className="rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="nth-select"
              onChange={(e) => setN(Number(e.target.value))}
              value={n}
            >
              {N_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(strings.nthWordOption, { n: option })}
                </option>
              ))}
            </select>
            <p className="text-muted-foreground text-xs">
              {t(strings.skipNote, { min: MIN_BLANK_LENGTH })}
            </p>
          </div>
        )}
        {mode === "list" && (
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="target-input"
            >
              {strings.wordsToBlankCommaSeparated}
            </label>
            <input
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id="target-input"
              onChange={(e) => setTargetInput(e.target.value)}
              placeholder={strings.photosynthesisChlorophyllGlucose}
              type="text"
              value={targetInput}
            />
          </div>
        )}
      </section>
      {/* Actions */}
      <div className="flex flex-wrap gap-3 print:hidden">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={!canGenerate}
          onClick={handleGenerate}
          type="button"
        >
          {worksheet ? strings.regenerate : strings.generateWorksheet}
        </button>
        {worksheet && (
          <>
            <button
              className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copyWorksheet}
            </button>
            <button
              className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
              onClick={() => window.print()}
              type="button"
            >
              {strings.print}
            </button>
            <button
              className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
              onClick={() => setShowBank((prev) => !prev)}
              type="button"
            >
              {showBank ? strings.hideWordBank : strings.showWordBank}
            </button>
            <button
              className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
              onClick={() => setShowAnswers((prev) => !prev)}
              type="button"
            >
              {showAnswers ? strings.hideAnswerKey : strings.showAnswerKey}
            </button>
          </>
        )}
      </div>
      {/* Worksheet (printable) */}
      {worksheet && worksheet.answers.length === 0 && (
        <p className="rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground text-sm print:hidden">
          {strings.noWordsWereBlankedTry}
        </p>
      )}
      {worksheet && worksheet.answers.length > 0 && (
        <section className="space-y-6">
          <div className="space-y-2">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.worksheet}
            </h2>
            <p className="whitespace-pre-wrap rounded-lg border border-border bg-card p-5 text-foreground text-sm leading-loose print:rounded-none print:border-0 print:p-0">
              {worksheet.text}
            </p>
          </div>
          {showBank && (
            <div className="space-y-2">
              <h2 className="font-semibold text-foreground text-lg">
                {strings.wordBank}
              </h2>
              <p className="rounded-lg border border-border bg-card p-5 text-foreground text-sm leading-relaxed print:rounded-none print:border-0 print:p-0">
                {worksheet.bank.join("  ·  ")}
              </p>
            </div>
          )}
          {showAnswers && (
            <div className="space-y-2">
              <h2 className="font-semibold text-foreground text-lg">
                {strings.answerKey}
              </h2>
              <ol className="grid grid-cols-2 gap-x-6 gap-y-1 rounded-lg border border-border bg-card p-5 sm:grid-cols-3 print:rounded-none print:border-0 print:p-0">
                {worksheet.answers.map((answer, i) => (
                  <li
                    className="text-muted-foreground text-sm print:text-foreground"
                    key={`answer-${i + 1}-${answer}`}
                  >
                    {i + 1}. {answer}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </section>
      )}
    </>
  );
}
